// stereo-core: pole density on the sphere, sampled on a projection-plane grid.
// Density is computed on the SPHERE (angular distance), then shown on the net,
// so values do not depend on the projection. Units: % of total per 1% area.
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
import { Vec3, unit } from "./vec.js";
import { Projection, unproject } from "./projection.js";

/** fisher-overlap = Fisher Overlap Influence (FOI), reproduces DIPS "Fisher";
 *  fisher = pointwise Fisher kernel exp(κ(cosθ−1)) normalised to the count-cone area;
 *  schmidt = pole count in the counting cone. */
export type DensityMethod = "fisher-overlap" | "fisher" | "schmidt";
export interface DensityGrid {
  size: number;            // grid is size x size over [-1, 1]^2
  values: Float64Array;    // row-major, row 0 = y = +1 (north) ... last = y = -1
  max: number;
  method: DensityMethod;
  kappa: number;
  countCircle: number;     // fraction of the hemisphere (0.01 = 1%)
}

/** 1% counting cone: 2π(1 - cos α) = 0.01 · 2π  →  cos α = 0.99 (α ≈ 8.11°). */
export const SCHMIDT_COS = 0.99;
// [DEN-01]
/** DIPS (Windows) uses a fixed grid spacing of 4% of the net radius -> 51 x 51 nodes.
 *  Using the same grid reproduces DIPS density values (verified: Schmidt 8.72%). */
export const DIPS_GRID_SIZE = 51;

/** Cone angle β of a counting circle covering fraction c of a hemisphere.
 *  "dips": β = √(2c) rad — the flat-disc relation πβ² = c·2π used by DIPS
 *          (Diederichs 1990, fig. 4.5). Reproduces all DIPS reference values.
 *  "exact": β = acos(1 − c) — true spherical-cap area. β differs by < 0.2 %
 *          (1 %: 8.1028° vs 8.1096°). */
export type ConeRule = "dips" | "exact";
// [DEN-02]
export const coneAngle = (c: number, rule: ConeRule = "dips") => (rule === "dips" ? Math.sqrt(2 * c) : Math.acos(1 - c));

// ---------- Fisher Overlap Influence (FOI) ----------
// β = acos(1 − c); K chosen so F(β)/F(0) = 0.05; Fisher truncated at β.
// w(ξ) = ∫₀^β F(φ) L(φ, ξ) dφ / (2π ∫₀^β F(φ) dφ), L = azimuthal span of the
// Fisher ring at φ lying inside the counting cone centred ξ away. w(0) = 1,
// w(ξ ≥ 2β) = 0. The integral uses dφ (no sin φ area element): this is the
// formulation that reproduces the DIPS reference values (see docs/theory.md).
export interface FOITable { beta: number; K: number; M: number; w: Float64Array }
const foiCache = new Map<string, FOITable>();
// [DEN-05]
export function fisherOverlapTable(c: number, rule: ConeRule = "dips", M = 400, J = 800): FOITable {
  const key = `${c}|${rule}|${M}`, hit = foiCache.get(key); if (hit) return hit;
  const beta = coneAngle(c, rule), cb = Math.cos(beta), K = Math.log(0.05) / (cb - 1);
  const F = (ph: number) => Math.exp(K * (Math.cos(ph) - 1));
  const dph = beta / J;
  let den = 0;
  for (let j = 0; j < J; j++) den += F(dph * (j + 0.5)) * dph;
  den *= 2 * Math.PI;
  const w = new Float64Array(M + 1);
  for (let i = 0; i <= M; i++) {
    const xi = (2 * beta * i) / M, sx = Math.sin(xi), cx = Math.cos(xi);
    let num = 0;
    for (let j = 0; j < J; j++) {
      const ph = dph * (j + 0.5), B = Math.sin(ph) * sx, A = Math.cos(ph) * cx;
      let L: number;
      if (B < 1e-15) L = A >= cb ? 2 * Math.PI : 0;
      else { const x = (cb - A) / B; L = x <= -1 ? 2 * Math.PI : x >= 1 ? 0 : 2 * Math.acos(x); }
      num += F(ph) * L * dph;
    }
    w[i] = num / den;
  }
  const t = { beta, K, M, w }; foiCache.set(key, t); return t;
}
/** Influence of one observation at angular separation xi (radians). */
export function fisherOverlapInfluence(t: FOITable, xi: number): number {
  const lim = 2 * t.beta; if (xi >= lim) return 0;
  const u = (xi / lim) * t.M, i = Math.floor(u);
  return t.w[i] + (t.w[Math.min(t.M, i + 1)] - t.w[i]) * (u - i);
}

// [DEN-01] [DEN-03] [DEN-04] [DEN-05]
export function densityGrid(poles: Vec3[], p: Projection, opts: {
  size?: number; method?: DensityMethod; kappa?: number; weights?: number[]; countCircle?: number; coneRule?: ConeRule;
} = {}): DensityGrid {
  const size = opts.size ?? 121;
  const method = opts.method ?? "fisher-overlap";
  const c = opts.countCircle ?? 0.01;
  const kappa = opts.kappa ?? 1 / c; // pointwise kernel: κ = 1/c (100 at 1%)
  const rule = opts.coneRule ?? "dips";
  const cosCone = Math.cos(coneAngle(c, rule));
  const foi = method === "fisher-overlap" ? fisherOverlapTable(c, rule) : null;
  const n = poles.length;
  const px = new Float64Array(n), py = new Float64Array(n), pz = new Float64Array(n);
  const w = new Float64Array(n);
  let W = 0;
  for (let j = 0; j < n; j++) {
    const v = unit(poles[j]);
    px[j] = v[0]; py[j] = v[1]; pz[j] = v[2];
    w[j] = opts.weights ? opts.weights[j] : 1; W += w[j];
  }
  // pointwise Fisher normalisation: integral of one kernel = the counting-cone area
  // (uniform data -> 100·c, i.e. 1 at 1%); see docs/theory.md
  const fisherScale = (100 * c * kappa) / (1 - Math.exp(-kappa));
  const values = new Float64Array(size * size);
  let max = 0;
  for (let r = 0; r < size; r++) {
    const y = 1 - (2 * r) / (size - 1);
    for (let c = 0; c < size; c++) {
      const x = -1 + (2 * c) / (size - 1);
      const g = unproject({ x, y }, p); // clamped to the rim outside the circle
      let s = 0;
      if (method === "schmidt") {
        for (let j = 0; j < n; j++) {
          if (Math.abs(g[0] * px[j] + g[1] * py[j] + g[2] * pz[j]) >= cosCone) s += w[j];
        }
        s = W > 0 ? (100 * s) / W : 0;
      } else if (foi) {
        const cLim = Math.cos(2 * foi.beta);
        for (let j = 0; j < n; j++) {
          const cth = Math.min(1, Math.abs(g[0] * px[j] + g[1] * py[j] + g[2] * pz[j]));
          if (cth > cLim) s += w[j] * fisherOverlapInfluence(foi, Math.acos(cth));
        }
        s = W > 0 ? (100 * s) / W : 0;
      } else {
        for (let j = 0; j < n; j++) {
          const cth = Math.abs(g[0] * px[j] + g[1] * py[j] + g[2] * pz[j]);
          s += w[j] * Math.exp(kappa * (cth - 1));
        }
        s = W > 0 ? (fisherScale * s) / W : 0;
      }
      values[r * size + c] = s;
      if (x * x + y * y <= 1 && s > max) max = s;
    }
  }
  return { size, values, max, method, kappa, countCircle: c };
}

// [DEN-07]
/** Bilinear sample of the grid at net coordinates. */
export function sampleGrid(g: DensityGrid, x: number, y: number): number {
  const fc = ((x + 1) / 2) * (g.size - 1), fr = ((1 - y) / 2) * (g.size - 1);
  const c0 = Math.max(0, Math.min(g.size - 2, Math.floor(fc)));
  const r0 = Math.max(0, Math.min(g.size - 2, Math.floor(fr)));
  const tc = Math.min(1, Math.max(0, fc - c0)), tr = Math.min(1, Math.max(0, fr - r0));
  const v = g.values, s = g.size;
  const a = v[r0 * s + c0], b = v[r0 * s + c0 + 1], c = v[(r0 + 1) * s + c0], d = v[(r0 + 1) * s + c0 + 1];
  return (a * (1 - tc) + b * tc) * (1 - tr) + (c * (1 - tc) + d * tc) * tr;
}

// [DEN-06]
/** Evenly spaced contour levels between 0 and max (exclusive). */
export function autoLevels(max: number, count = 8): number[] {
  if (!(max > 0)) return [];
  return Array.from({ length: count - 1 }, (_, i) => (max * (i + 1)) / count);
}
